/**
 * Product Personalization — core
 *
 * Core behavior is theme-agnostic. Theme-specific selectors/events live in THEME_ADAPTER.
 * Pair with product-personalization-core.css. Brand chrome belongs in
 * product-personalization-theme.css (host theme).
 *
 * Preview image upload (canvas + Uploadcare) lives in
 * product-personalization-preview-image.js and hooks confirm via
 * `personalization:before-confirm` + detail.waitUntil(promise).
 *
 * Future extraction: move this file + core CSS + Liquid snippet into a standalone package.
 */

(() => {
  // ---------------------------------------------------------------------------
  // Theme adapter — client-specific integration points
  // ---------------------------------------------------------------------------
  const THEME_ADAPTER = {
    productFormSelector: (formId) =>
      formId
        ? document.getElementById(formId) || document.querySelector(`form#${CSS.escape(formId)}`)
        : document.getElementById("AddToCartForm") ||
          document.querySelector('form[action="/cart/add"]'),
    productChangeEvent: "personalization:product-change",
    variantSelectSelector: "#product-select",
    productMainSelector: "product-main",
    cartCountSelector: ".cart-count, #cart-count a:first-child, #gocart p a, #cart .checkout em, .item-count",
    cartLinkSelector: ".cart-link",
    cartAddUrl: "/cart/add.js",
    cartUrl: "/cart.js",
    cartPageUrl: "/cart",
    // Match Shopify.AjaxifyCart: reveal the header cart badge and sync count chrome.
    onCartUpdated(cart, { addedQuantity } = {}) {
      const count = cart?.item_count;
      if (count == null) return;

      document.querySelectorAll(THEME_ADAPTER.cartCountSelector).forEach((el) => {
        el.classList.toggle("hidden-count", count <= 0);
        if (!el.classList.contains("cart-count")) {
          el.textContent = String(count);
        }
      });

      const cartLabel =
        count > 0
          ? `View cart, ${count} item${count !== 1 ? "s" : ""}`
          : "View cart";
      document.querySelectorAll(THEME_ADAPTER.cartLinkSelector).forEach((el) => {
        el.setAttribute("aria-label", cartLabel);
      });

      if (typeof window.announceCartItemUpdate === "function") {
        window.announceCartItemUpdate(addedQuantity || 1);
      }

      const params = new URLSearchParams({
        name: "otm_customer_cart_count",
        value: String(count),
      });
      fetch(`https://cookie.overthemoon.com/set/?${params}`, {
        credentials: "include",
      }).catch(() => {});
    },
  };

  // ---------------------------------------------------------------------------
  // Core constants
  // ---------------------------------------------------------------------------
  const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    'input:not([disabled]):not([type="hidden"])',
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  // Default font styles — stand-in when no style metaobjects are configured.
  // Contract: { id, label, family, weight, maxCharacters, maxFontSize, capitalization, monogramFontStyle, initialText }
  const DEFAULT_FONTS = [
    {
      id: "bold",
      label: "Bold",
      family: "Arial, Helvetica, sans-serif",
      weight: "700",
      maxCharacters: 20,
      maxFontSize: 48,
      capitalization: "none",
      monogramFontStyle: false,
      initialText: "",
    },
    {
      id: "light",
      label: "Light",
      family: "Arial, Helvetica, sans-serif",
      weight: "300",
      maxCharacters: 20,
      maxFontSize: 48,
      capitalization: "none",
      monogramFontStyle: false,
      initialText: "",
    },
  ];

  const PROPERTY_KEYS = {
    timeStamp: "_time_stamp",
    placement: "Embroidery Placement",
    text: "My Text",
    style: "Embroidery Font",
    color: "Embroidery Color",
  };

  const VERSION = "1";

  // ---------------------------------------------------------------------------
  // Utilities (core)
  // ---------------------------------------------------------------------------
  function parseJSON(el, fallback) {
    if (!el) return fallback;
    try {
      const raw = el.textContent.trim();
      if (!raw) return fallback;
      return JSON.parse(raw);
    } catch (err) {
      console.warn("product-personalization: invalid JSON", err);
      return fallback;
    }
  }

  function normalizeProduct(data) {
    const image = data?.image || {};
    return {
      id: data?.id != null ? String(data.id) : "",
      handle: data?.handle || "",
      title: data?.title || "",
      vendor: data?.vendor || "",
      imageSource: data?.imageSource || "variant",
      image: {
        id: image.id != null ? String(image.id) : "",
        url: image.url || "",
        width: Number(image.width) || 0,
        height: Number(image.height) || 0,
        alt: image.alt || "",
        focalX: clampNumber(image.focalX, 0, 100, 50),
        focalY: clampNumber(image.focalY, 0, 100, 50),
      },
      images: Array.isArray(data?.images) ? data.images.map(normalizeImage) : [],
      variants: Array.isArray(data?.variants) ? data.variants : [],
    };
  }

  function normalizeImage(image) {
    return {
      id: image?.id != null ? String(image.id) : "",
      url: image?.url || "",
      width: Number(image?.width) || 0,
      height: Number(image?.height) || 0,
      alt: image?.alt || "",
      focalX: clampNumber(image?.focalX, 0, 100, 50),
      focalY: clampNumber(image?.focalY, 0, 100, 50),
      srcPath: image?.srcPath || "",
    };
  }

  function clampNumber(value, min, max, fallback) {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, n));
  }

  /** Format cents like Liquid money_without_trailing_zeros (e.g. $148 or $148.50). */
  function formatMoney(cents) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return "";

    const normalize = (value) => {
      if (typeof value !== "string" || !value.trim()) return "";
      return value.replace(/\d[\d,]*(?:\.\d+)?/g, (numStr) => {
        const amount = Number(numStr.replace(/,/g, ""));
        if (Number.isNaN(amount)) return numStr;
        const hasCents = Math.round(amount * 100) % 100 !== 0;
        return amount.toLocaleString(undefined, {
          minimumFractionDigits: hasCents ? 2 : 0,
          maximumFractionDigits: hasCents ? 2 : 0,
        });
      });
    };

    if (typeof window.Shopify?.formatMoney === "function") {
      const formatted = normalize(window.Shopify.formatMoney(n));
      if (formatted) return formatted;
    }

    const amount = n / 100;
    const hasCents = Math.round(Math.abs(amount) * 100) % 100 !== 0;
    return (
      "$" +
      amount.toLocaleString(undefined, {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: hasCents ? 2 : 0,
      })
    );
  }

  /** Quote custom family names so stacks like `Perry, sans-serif` parse reliably. */
  function quoteFontFamily(stack) {
    return String(stack || "")
      .split(",")
      .map((part) => {
        const name = part.trim().replace(/^['"]|['"]$/g, "");
        if (!name) return "";
        if (
          /^(serif|sans-serif|monospace|cursive|fantasy|system-ui|inherit|initial|unset)$/i.test(
            name
          )
        ) {
          return name;
        }
        return `'${name.replace(/\\/g, "\\\\").replace(/'/g, "\\'")}'`;
      })
      .filter(Boolean)
      .join(", ");
  }

  /** Metaobject choice: None | Uppercase → none | uppercase */
  function normalizeCapitalization(value) {
    const v = String(value || "none").trim().toLowerCase();
    if (v === "uppercase" || v === "upper") return "uppercase";
    return "none";
  }

  function applyCapitalization(text, capitalization) {
    const value = String(text || "");
    return normalizeCapitalization(capitalization) === "uppercase"
      ? value.toUpperCase()
      : value;
  }

  function fontInitialText(font) {
    return String(font?.initialText || "").trim();
  }

  /** True when the shopper has not replaced this style's metaobject initial text. */
  function isUneditedInitialText(text, font) {
    const raw = String(text || "").trim();
    const initial = fontInitialText(font);
    return !raw || raw === initial;
  }

  /** Handleize a font label/family for data-attribute CSS hooks (matches Liquid handleize). */
  function fontHandle(font) {
    const raw = String(font?.family || font?.label || font?.id || "")
      .split(",")[0]
      .replace(/['"]/g, "")
      .trim()
      .toLowerCase();
    return raw.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }

  /** Fulfillment timestamp: YYYYMMDDHHmm (local time). */
  function buildTimeStamp(date = new Date()) {
    const pad = (n) => String(n).padStart(2, "0");
    return [
      date.getFullYear(),
      pad(date.getMonth() + 1),
      pad(date.getDate()),
      pad(date.getHours()),
      pad(date.getMinutes()),
    ].join("");
  }

  function isMonogramEnabled(font) {
    return Boolean(font?.monogramFontStyle);
  }

  function shouldRenderMonogram(font, text) {
    return isMonogramEnabled(font) && String(text || "").length === 3;
  }

  /** Coerce Liquid/JSON color drops into a CSS color string. */
  function normalizeCssColor(value) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (value && typeof value === "object") {
      if (typeof value.hex === "string" && value.hex) return value.hex;
      if (typeof value.rgb === "string" && value.rgb) return value.rgb;
      if (value.red != null && value.green != null && value.blue != null) {
        return `rgb(${value.red}, ${value.green}, ${value.blue})`;
      }
    }
    return "#1a1a1a";
  }

  /** Shrink single-line text to fit the zone. minSize is a technical floor (preview uses 1). */
  function fitSingleLineText(el, zone, minSize, maxSize) {
    if (!el || !zone) return { fits: true, size: maxSize };

    const text = (el.textContent || "").trim();
    if (!text) {
      el.style.fontSize = `${maxSize}px`;
      return { fits: true, size: maxSize };
    }

    const availableWidth = zone.clientWidth;
    const availableHeight = zone.clientHeight;
    if (availableWidth <= 0 || availableHeight <= 0) {
      return { fits: true, size: maxSize };
    }

    let size = maxSize;
    el.style.whiteSpace = "nowrap";
    el.style.fontSize = `${size}px`;

    while (size > minSize) {
      const tooWide = el.scrollWidth > availableWidth;
      const tooTall = el.scrollHeight > availableHeight;
      if (!tooWide && !tooTall) break;
      size -= 1;
      el.style.fontSize = `${size}px`;
    }

    const fits =
      el.scrollWidth <= availableWidth + 0.5 &&
      el.scrollHeight <= availableHeight + 0.5;

    return { fits, size };
  }

  let measureCanvas;

  /**
   * Glyph ink size including script swashes that layout width (scrollWidth) misses.
   */
  function measureTextInk(el) {
    if (!el) return { width: 0, height: 0 };

    const text = el.textContent || "";
    if (!text.trim()) return { width: 0, height: 0 };

    const style = getComputedStyle(el);
    if (!measureCanvas) measureCanvas = document.createElement("canvas");
    const ctx = measureCanvas.getContext("2d");
    ctx.font = style.font;
    if (ctx.letterSpacing !== undefined) {
      ctx.letterSpacing = style.letterSpacing;
    }

    const metrics = ctx.measureText(text);
    const inkWidth =
      (metrics.actualBoundingBoxLeft || 0) + (metrics.actualBoundingBoxRight || 0);
    const inkHeight =
      (metrics.actualBoundingBoxAscent || 0) +
      (metrics.actualBoundingBoxDescent || 0);

    let rangeWidth = 0;
    let rangeHeight = 0;
    const range = document.createRange();
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (!node.textContent) continue;
      range.selectNodeContents(node);
      const rects = [...range.getClientRects()];
      rects.forEach((rect) => {
        rangeWidth = Math.max(rangeWidth, rect.width);
        rangeHeight = Math.max(rangeHeight, rect.height);
      });
    }

    const advance = metrics.width || 0;
    const overflowLeft = Math.max(0, metrics.actualBoundingBoxLeft || 0);
    const overflowRight = Math.max(0, (metrics.actualBoundingBoxRight || 0) - advance);

    return {
      width: Math.max(advance, inkWidth, rangeWidth),
      height: Math.max(inkHeight, rangeHeight),
      advance,
      overflowLeft,
      overflowRight,
    };
  }

  // ---------------------------------------------------------------------------
  // Web Component
  // ---------------------------------------------------------------------------
  class ProductPersonalization extends HTMLElement {
    constructor() {
      super();
      this.state = {
        product: normalizeProduct({}),
        personalization: {
          enabled: false,
          confirmed: false,
          text: "",
          font: "bold",
          color: "default",
        },
        extraProperties: {},
        ui: {
          open: false,
          error: "",
          formError: "",
        },
      };
      this.config = {
        required: false,
        maxCharacters: 20,
        maxFontSize: 48,
        colors: [],
        fonts: DEFAULT_FONTS.slice(),
        embroideryPlacement: "",
        version: VERSION,
      };
      this.els = {};
      this._bound = {};
      this._resizeObserver = null;
      this._openingTrigger = null;
      this._formSubmitBound = false;
      this._confirmInFlight = false;
      this._focusTrapActive = false;
    }

    // -----------------------------------------------------------------------
    // Lifecycle
    // -----------------------------------------------------------------------
    connectedCallback() {
      this._cacheElements();
      this._loadData();
      this._bindEvents();
      this._bindThemeAdapter();
      this._observeResize();
      this.updateState();
      this._syncProperties();
    }

    disconnectedCallback() {
      this._deactivateFocusTrap();
      this._unlockScroll();
      this._unbindEvents();
      this._unbindThemeAdapter();
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      this._removeFormSubmitListener();

      const form = this._getProductForm();
      form
        ?.querySelectorAll(
          "[data-personalization-property], [data-personalization-additional-item]"
        )
        .forEach((el) => el.remove());
    }

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------
    setProduct(productData, options = {}) {
      const preserve = options.preservePersonalization === true;
      const next = normalizeProduct(productData);

      // Metaobject preview image is owned by personalization config, not PDP variants.
      if (
        (this.state.product.imageSource || this.config.imageSource) === "metaobject" &&
        this.state.product.image?.url
      ) {
        next.imageSource = "metaobject";
        next.image = { ...this.state.product.image };
        next.images =
          Array.isArray(this.state.product.images) && this.state.product.images.length
            ? this.state.product.images.map((img) => ({ ...img }))
            : next.images;
      }

      this.state.product = next;

      if (!preserve) {
        this.state.personalization = {
          enabled: false,
          confirmed: false,
          text: this.config.defaultText || "",
          font: this.config.fonts[0]?.id || "bold",
          color: this.config.colors[0]?.id || "",
        };
        this.state.extraProperties = {};
        this.state.ui.error = "";
        this.state.ui.formError = "";
      }

      this.updateState();
      this._syncProperties();
      this._fitText();
    }

    open(trigger) {
      const dialog = this.els.dialog;
      if (!dialog) return;

      this._openingTrigger = trigger || this.els.openBtn || document.activeElement;
      this.state.ui.open = true;
      this.state.ui.error = "";
      this.state.ui.formError = "";

      if (this._closeTimer) {
        window.clearTimeout(this._closeTimer);
        this._closeTimer = 0;
      }
      this._isClosing = false;
      dialog.classList.remove("is-closing");

      if (typeof dialog.showModal === "function") {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }

      this._lockScroll();
      this._activateFocusTrap();
      this.updateState();
      requestAnimationFrame(() => {
        this._fitText();
        this.els.textInput?.focus({ preventScroll: true });
        this._resetDialogScroll();
        requestAnimationFrame(() => this._resetDialogScroll());
      });
    }

    _resetDialogScroll() {
      [
        this.els.dialog,
        this.querySelector(".product-personalization__layout"),
        this.querySelector(".product-personalization__controls-col"),
        this.querySelector(".product-personalization__controls-body"),
      ].forEach((el) => {
        if (el) el.scrollTop = 0;
      });
    }

    close() {
      const dialog = this.els.dialog;
      if (this._isClosing) return;

      const canUseNativeClose =
        dialog && typeof dialog.close === "function" && dialog.open;

      if (!canUseNativeClose) {
        this._completeClose();
        if (dialog) dialog.removeAttribute("open");
        return;
      }

      if (this._prefersReducedMotion()) {
        dialog.close();
        return;
      }

      this._isClosing = true;
      dialog.classList.add("is-closing");

      const finish = (event) => {
        if (event && event.target !== dialog) return;
        if (!this._isClosing) return;
        this._isClosing = false;
        dialog.removeEventListener("animationend", finish);
        if (this._closeTimer) {
          window.clearTimeout(this._closeTimer);
          this._closeTimer = 0;
        }
        dialog.classList.remove("is-closing");
        if (dialog.open) dialog.close();
      };

      dialog.addEventListener("animationend", finish);
      this._closeTimer = window.setTimeout(finish, 400);
    }

    _completeClose() {
      this.state.ui.open = false;
      this._deactivateFocusTrap();
      this._unlockScroll();
      this.updateState();
      this._restoreTriggerFocus();
    }

    _prefersReducedMotion() {
      return Boolean(
        window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
      );
    }

    _restoreTriggerFocus() {
      const confirmed =
        this.state.personalization.enabled &&
        this.state.personalization.confirmed;
      const preferred = confirmed ? this.els.editBtn : this.els.openBtn;
      const trigger = this._openingTrigger;
      this._openingTrigger = null;
      const target = [preferred, trigger, this.els.openBtn].find(
        (el) => el && !el.hidden && typeof el.focus === "function"
      );
      target?.focus();
    }

    _lockScroll() {
      if (this._scrollLocked) return;
      this._scrollLocked = true;
      this._lockedScrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add("product-personalization-scroll-lock");
      document.body.classList.add("product-personalization-scroll-lock");
      document.body.style.top = `-${this._lockedScrollY}px`;
    }

    _unlockScroll() {
      if (!this._scrollLocked) return;
      this._scrollLocked = false;
      const y = this._lockedScrollY || 0;
      this._lockedScrollY = 0;
      document.documentElement.classList.remove("product-personalization-scroll-lock");
      document.body.classList.remove("product-personalization-scroll-lock");
      document.body.style.top = "";
      window.scrollTo(0, y);
    }

    _getFocusableElements() {
      const dialog = this.els.dialog;
      if (!dialog) return [];
      return [...dialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter((el) => {
        if (el.hasAttribute("disabled") || el.getAttribute("aria-hidden") === "true") {
          return false;
        }
        if (el.closest("[hidden]")) return false;
        const style = window.getComputedStyle(el);
        if (style.display === "none" || style.visibility === "hidden") return false;
        return el.getClientRects().length > 0;
      });
    }

    _activateFocusTrap() {
      if (this._focusTrapActive) return;
      const dialog = this.els.dialog;
      if (!dialog) return;

      this._focusTrapActive = true;
      if (!dialog.hasAttribute("tabindex")) {
        dialog.setAttribute("tabindex", "-1");
      }
      this._bound.onFocusTrapKeydown = (e) => this._handleFocusTrapKeydown(e);
      this._bound.onFocusTrapIn = (e) => this._handleFocusTrapIn(e);
      dialog.addEventListener("keydown", this._bound.onFocusTrapKeydown);
      document.addEventListener("focusin", this._bound.onFocusTrapIn);
    }

    _deactivateFocusTrap() {
      if (!this._focusTrapActive) return;
      this._focusTrapActive = false;
      this.els.dialog?.removeEventListener("keydown", this._bound.onFocusTrapKeydown);
      document.removeEventListener("focusin", this._bound.onFocusTrapIn);
    }

    _handleFocusTrapKeydown(event) {
      if (event.key !== "Tab") return;

      const nodes = this._getFocusableElements();
      if (!nodes.length) {
        event.preventDefault();
        this.els.dialog?.focus();
        return;
      }

      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !this.els.dialog?.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    _handleFocusTrapIn(event) {
      const dialog = this.els.dialog;
      if (!dialog || !this.state.ui.open) return;
      if (dialog.contains(event.target)) return;

      const nodes = this._getFocusableElements();
      (nodes[0] || dialog).focus();
    }

    reset() {
      this.state.personalization = {
        enabled: false,
        confirmed: false,
        text: this.config.defaultText || "",
        font: this.config.fonts[0]?.id || "bold",
        color: this.config.colors[0]?.id || "",
      };
      this.state.extraProperties = {};
      this.state.ui.error = "";
      this.state.ui.formError = "";
      this.updateState();
      this._syncProperties();
      this.close();
    }

    /**
     * Confirm personalization, then add to cart the same way as #AddToCart
     * and redirect to the cart page. Dispatches `personalization:before-confirm`
     * so adapters can await async work via detail.waitUntil(promise). Returned
     * `{ properties }` objects are merged into hidden line-item inputs.
     */
    async confirm() {
      if (this._confirmInFlight) return false;

      const font = this._getFont(this.state.personalization.font);
      let text = (this.state.personalization.text || "").trim();
      text = applyCapitalization(text, font.capitalization);
      this.state.personalization.text = text;

      if (!text) {
        this.state.ui.error = "Please enter personalization text.";
        this.updateState();
        this.els.textInput?.focus();
        return false;
      }

      const limits = this._getStyleLimits();
      if (text.length > limits.maxCharacters) {
        this.state.ui.error = `Text must be ${limits.maxCharacters} characters or fewer.`;
        this.updateState();
        this.els.textInput?.focus();
        return false;
      }

      const fit = this._fitText();
      if (!fit.fits) {
        this.state.ui.error = "Text is too long to fit on the product. Shorten it or choose another style.";
        this.updateState();
        this.els.textInput?.focus();
        return false;
      }

      this._confirmInFlight = true;
      const waiters = [];
      this.dispatchEvent(
        new CustomEvent("personalization:before-confirm", {
          bubbles: true,
          cancelable: true,
          detail: {
            component: this,
            waitUntil(promise) {
              waiters.push(Promise.resolve(promise));
            },
          },
        })
      );

      const extraProperties = {};
      try {
        try {
          if (waiters.length) {
            const results = await Promise.all(waiters);
            results.forEach((result) => {
              if (result?.properties && typeof result.properties === "object") {
                Object.assign(extraProperties, result.properties);
              }
            });
          }
        } catch (err) {
          console.error("product-personalization: before-confirm failed", err);
        }

        this.state.extraProperties = extraProperties;
        this.state.personalization.enabled = true;
        this.state.personalization.confirmed = true;
        this.state.ui.error = "";
        this.state.ui.formError = "";
        this.updateState();
        this._syncProperties();

        const added = await this._addToCartFromConfirm();
        if (added) {
          window.location.assign(this._getCartPageUrl());
          return true;
        }
        return false;
      } catch (err) {
        console.error("product-personalization: add to cart failed", err);
        this.state.ui.error =
          this.state.ui.error || "Could not add personalized item to your bag.";
        this.updateState();
        this._setConfirmBusy(false);
        return false;
      } finally {
        this._confirmInFlight = false;
      }
    }

    // -----------------------------------------------------------------------
    // State / DOM
    // -----------------------------------------------------------------------
    updateState() {
      const { product, personalization, ui } = this.state;
      const font = this._getFont(personalization.font);
      const color = this._getColor(personalization.color);

      if (this.els.image && product.image.url) {
        if (this.els.image.getAttribute("src") !== product.image.url) {
          this.els.image.src = product.image.url;
        }
        this.els.image.alt = product.image.alt || product.title || "";
        if (product.image.width) this.els.image.width = product.image.width;
        if (product.image.height) this.els.image.height = product.image.height;
      }

      if (this.els.productVendor) {
        this.els.productVendor.textContent = product.vendor || "";
      }
      if (this.els.productTitle) {
        this.els.productTitle.textContent = product.title || "";
      }

      if (this.els.zone) {
        this.els.zone.style.setProperty(
          "--personalization-focal-x",
          `${product.image.focalX}%`
        );
        this.els.zone.style.setProperty(
          "--personalization-focal-y",
          `${product.image.focalY}%`
        );
      }

      if (this.els.previewText) {
        const rawText = (personalization.text || "").trim();
        const displayText = applyCapitalization(
          rawText || fontInitialText(font),
          font.capitalization
        );
        this._renderPreviewText(displayText, font);
        this.els.previewText.classList.toggle(
          "product-personalization__preview-text--sample",
          !rawText
        );
        this._applyFontToElement(this.els.previewText, font);
        this.els.previewText.style.fontStyle = font.style || "normal";
        this.els.previewText.style.textTransform =
          normalizeCapitalization(font.capitalization) === "uppercase"
            ? "uppercase"
            : "none";
        this.els.previewText.style.color = color.value;
        this.style.setProperty("--personalization-overlay-color", color.value);
      }

      this._updateFontOptionSamples(personalization.text, color.value);

      if (this.els.textInput) {
        this.els.textInput.style.textTransform =
          normalizeCapitalization(font.capitalization) === "uppercase"
            ? "uppercase"
            : "none";
        if (this.els.textInput.value !== personalization.text) {
          this.els.textInput.value = personalization.text;
        }
      }

      this._applyStyleLimits();

      if (this.els.charCount) {
        this.els.charCount.textContent = String(personalization.text.length);
      }

      this.querySelectorAll("[data-personalization-font]").forEach((input) => {
        input.checked = String(input.value) === String(personalization.font);
      });

      this.querySelectorAll("[data-personalization-color]").forEach((input) => {
        input.checked = String(input.value) === String(personalization.color);
      });

      if (this.els.selectedColor) {
        const colorLabel = (color.label || "").trim();
        this.els.selectedColor.hidden = !colorLabel;
        if (this.els.selectedColorLabel) {
          this.els.selectedColorLabel.textContent = colorLabel;
        }
      }

      if (this.els.error) {
        const hasError = Boolean(ui.error);
        this.els.error.hidden = !hasError;
        this.els.error.textContent = ui.error || "";
      }

      if (this.els.textInput) {
        this.els.textInput.setAttribute("aria-invalid", ui.error ? "true" : "false");
        const describedBy = [
          this.els.charCount?.closest(".product-personalization__char-count")?.id,
          ui.error && this.els.error?.id,
        ]
          .filter(Boolean)
          .join(" ");
        if (describedBy) {
          this.els.textInput.setAttribute("aria-describedby", describedBy);
        } else {
          this.els.textInput.removeAttribute("aria-describedby");
        }
      }

      if (this.els.formError) {
        const hasFormError = Boolean(ui.formError);
        this.els.formError.hidden = !hasFormError;
        this.els.formError.textContent = ui.formError || "";
      }

      if (this.els.summary) {
        const show =
          personalization.enabled &&
          personalization.confirmed &&
          Boolean(personalization.text);
        this.els.summary.hidden = !show;
        if (this.els.openBtn) this.els.openBtn.hidden = show;
        if (show) {
          const displayText = applyCapitalization(
            personalization.text,
            font.capitalization
          );
          if (this.els.summaryPreview) {
            this._renderPreviewText(displayText, font, this.els.summaryPreview);
            this._applyFontToElement(this.els.summaryPreview, font);
            this.els.summaryPreview.style.fontStyle = font.style || "normal";
            this.els.summaryPreview.style.textTransform =
              normalizeCapitalization(font.capitalization) === "uppercase"
                ? "uppercase"
                : "none";
            this.els.summaryPreview.style.color = color.value;
            this.els.summaryPreviewZone?.setAttribute(
              "data-personalization-summary-preview-font",
              fontHandle(font)
            );
          }
          if (this.els.summaryHeading) {
            this.els.summaryHeading.textContent = displayText;
          }
          if (this.els.summaryStyle) {
            this.els.summaryStyle.textContent = font.label || "";
          }
          if (this.els.summaryColor) {
            this.els.summaryColor.textContent = color.label || "";
          }
        }
      }

      this._syncConfirmPrice();
      this._fitText();
    }

    // -----------------------------------------------------------------------
    // Private helpers
    // -----------------------------------------------------------------------
    _cacheElements() {
      this.els = {
        productData: this.querySelector("[data-personalization-product]"),
        configData: this.querySelector("[data-personalization-config]"),
        openBtn: this.querySelector("[data-personalization-open]"),
        editBtn: this.querySelector("[data-personalization-edit]"),
        dialog: this.querySelector("[data-personalization-dialog]"),
        closeBtns: [...this.querySelectorAll("[data-personalization-close]")],
        cancelBtn: this.querySelector("[data-personalization-cancel]"),
        productVendor: this.querySelector("[data-personalization-vendor]"),
        productTitle: this.querySelector("[data-personalization-title]"),
        confirmBtn: this.querySelector("[data-personalization-confirm]"),
        confirmPrice: this.querySelector("[data-personalization-confirm-price]"),
        textInput: this.querySelector("[data-personalization-text]"),
        charCount: this.querySelector("[data-personalization-char-count]"),
        charMax: this.querySelector("[data-personalization-char-max]"),
        preview: this.querySelector("[data-personalization-preview]"),
        image: this.querySelector("[data-personalization-image]"),
        zone: this.querySelector("[data-personalization-zone]"),
        previewText: this.querySelector("[data-personalization-preview-text]"),
        selectedColor: this.querySelector("[data-personalization-selected-color]"),
        selectedColorLabel: this.querySelector("[data-personalization-selected-color-label]"),
        error: this.querySelector("[data-personalization-error]"),
        formError: this.querySelector("[data-personalization-form-error]"),
        summary: this.querySelector("[data-personalization-summary]"),
        summaryPreview: this.querySelector("[data-personalization-summary-preview]"),
        summaryPreviewZone: this.querySelector(
          ".product-personalization__summary-preview"
        ),
        summaryHeading: this.querySelector("[data-personalization-summary-heading]"),
        summaryStyle: this.querySelector("[data-personalization-summary-style]"),
        summaryColor: this.querySelector("[data-personalization-summary-color]"),
        removeBtn: this.querySelector("[data-personalization-remove]"),
      };
    }

    _loadData() {
      const productData = parseJSON(this.els.productData, {});
      const configData = parseJSON(this.els.configData, {});

      this.state.product = normalizeProduct(productData);
      const fonts = this._normalizeFonts(configData.fonts);
      this.config = {
        required:
          this.getAttribute("data-personalization-required") === "true" ||
          Boolean(configData.required),
        // Fallback defaults when a style omits limits; active values come from the selected style.
        maxCharacters: 20,
        maxFontSize: 48,
        colors: this._normalizeColors(configData.colors),
        fonts,
        version: configData.version || VERSION,
        imageSource: configData.imageSource || productData.imageSource || "variant",
        feeVariantId:
          configData.feeVariantId != null && configData.feeVariantId !== ""
            ? String(configData.feeVariantId)
            : null,
        feePrice: Math.max(0, Number(configData.feePrice) || 0),
        embroideryPlacement:
          configData.embroideryPlacement != null
            ? String(configData.embroideryPlacement).trim()
            : "",
        defaultText: this._readDefaultText(fonts),
      };

      // Sync selection from the first/checked radios so typing uses real CSS color values
      // without requiring a click first.
      const checkedColor = this.querySelector("[data-personalization-color]:checked");
      const firstColor = this.querySelector("[data-personalization-color]");
      const colorRadio = checkedColor || firstColor;
      if (colorRadio) {
        this.state.personalization.color = String(colorRadio.value);
        if (!checkedColor && firstColor) firstColor.checked = true;
      } else {
        this.state.personalization.color = this.config.colors[0]?.id || "";
      }

      const checkedFont = this.querySelector("[data-personalization-font]:checked");
      const firstFont = this.querySelector("[data-personalization-font]");
      const fontRadio = checkedFont || firstFont;
      if (fontRadio) {
        this.state.personalization.font = String(fontRadio.value);
        if (!checkedFont && firstFont) firstFont.checked = true;
      } else {
        this.state.personalization.font = this.config.fonts[0]?.id || "bold";
      }

      this.state.personalization.text = this.config.defaultText;

      if (this.els.textInput) {
        this._applyStyleLimits();
      }
    }

    _readDefaultText(fonts = this.config.fonts) {
      const fromInput = (this.els.textInput?.getAttribute("value") || "").trim();
      if (fromInput) return fromInput;
      return fontInitialText(fonts?.[0]);
    }

    _bindEvents() {
      this._bound.onOpen = (e) => {
        e.preventDefault();
        this.open(e.currentTarget);
      };
      this._bound.onClose = (e) => {
        e.preventDefault();
        this.close();
      };
      this._bound.onCancel = (e) => {
        e.preventDefault();
        this.reset();
      };
      this._bound.onRemove = (e) => {
        e.preventDefault();
        this.reset();
        this.els.openBtn?.focus();
      };
      this._bound.onConfirm = (e) => {
        e.preventDefault();
        this.confirm();
      };
      this._bound.onTextInput = (e) => {
        const limits = this._getStyleLimits();
        let value = e.target.value || "";
        if (value.length > limits.maxCharacters) {
          value = value.slice(0, limits.maxCharacters);
          e.target.value = value;
        }
        this.state.personalization.text = value;
        this.state.ui.error = "";
        this.updateState();
      };
      this._bound.onTextClick = () => this._clearUneditedDefaultText();
      this._bound.onTextKeydown = (e) => this._replaceUneditedDefaultTextOnKey(e);
      this._bound.onFontChange = (e) => {
        if (!e.target.matches("[data-personalization-font]")) return;
        const previousFont = this._getFont(this.state.personalization.font);
        const nextFont = this._getFont(e.target.value);
        this.state.personalization.font = e.target.value;
        this.state.ui.error = "";
        if (isUneditedInitialText(this.state.personalization.text, previousFont)) {
          const maxChars = Math.max(1, Number(nextFont.maxCharacters) || 20);
          this.state.personalization.text = fontInitialText(nextFont).slice(0, maxChars);
        }
        this._applyStyleLimits({ truncateText: true });
        this.updateState();
        this._loadActiveFont().then(() => this._fitText()).catch(() => {});
      };
      this._bound.onColorChange = (e) => {
        if (!e.target.matches("[data-personalization-color]")) return;
        this.state.personalization.color = e.target.value;
        this.state.ui.error = "";
        this.updateState();
      };
      this._bound.onDialogClose = () => {
        this._isClosing = false;
        this.els.dialog?.classList.remove("is-closing");
        this._completeClose();
      };
      this._bound.onDialogCancel = (e) => {
        e.preventDefault();
        this.close();
      };
      this._bound.onDialogClick = (e) => {
        const dialog = this.els.dialog;
        if (!dialog || e.target !== dialog) return;
        this.close();
      };
      this._bound.onImageLoad = () => this._fitText();
      this._bound.onFormSubmit = (e) => this._handleFormSubmit(e);

      this.els.openBtn?.addEventListener("click", this._bound.onOpen);
      this.els.editBtn?.addEventListener("click", this._bound.onOpen);
      this.els.removeBtn?.addEventListener("click", this._bound.onRemove);
      this.els.closeBtns?.forEach((btn) => {
        btn.addEventListener("click", this._bound.onClose);
      });
      this.els.cancelBtn?.addEventListener("click", this._bound.onCancel);
      this.els.confirmBtn?.addEventListener("click", this._bound.onConfirm);
      this.els.textInput?.addEventListener("input", this._bound.onTextInput);
      this.els.textInput?.addEventListener("click", this._bound.onTextClick);
      this.els.textInput?.addEventListener("keydown", this._bound.onTextKeydown);
      this.addEventListener("change", this._bound.onFontChange);
      this.addEventListener("change", this._bound.onColorChange);
      this.els.dialog?.addEventListener("close", this._bound.onDialogClose);
      this.els.dialog?.addEventListener("cancel", this._bound.onDialogCancel);
      this.els.dialog?.addEventListener("click", this._bound.onDialogClick);
      this.els.image?.addEventListener("load", this._bound.onImageLoad);

      this._attachFormSubmitListener();

      if (document.fonts?.ready) {
        document.fonts.ready
          .then(() => this._loadActiveFont())
          .then(() => this._fitText())
          .catch(() => {});
      }
    }

    _unbindEvents() {
      this.els.openBtn?.removeEventListener("click", this._bound.onOpen);
      this.els.editBtn?.removeEventListener("click", this._bound.onOpen);
      this.els.removeBtn?.removeEventListener("click", this._bound.onRemove);
      this.els.closeBtns?.forEach((btn) => {
        btn.removeEventListener("click", this._bound.onClose);
      });
      this.els.cancelBtn?.removeEventListener("click", this._bound.onCancel);
      this.els.confirmBtn?.removeEventListener("click", this._bound.onConfirm);
      this.els.textInput?.removeEventListener("input", this._bound.onTextInput);
      this.els.textInput?.removeEventListener("click", this._bound.onTextClick);
      this.els.textInput?.removeEventListener("keydown", this._bound.onTextKeydown);
      this.removeEventListener("change", this._bound.onFontChange);
      this.removeEventListener("change", this._bound.onColorChange);
      this.els.dialog?.removeEventListener("close", this._bound.onDialogClose);
      this.els.dialog?.removeEventListener("cancel", this._bound.onDialogCancel);
      this.els.dialog?.removeEventListener("click", this._bound.onDialogClick);
      this.els.image?.removeEventListener("load", this._bound.onImageLoad);
    }

    _bindThemeAdapter() {
      this._bound.onProductChange = (event) => {
        const detail = event.detail || {};
        if (!detail.product) return;

        // Scope to the nearest product-main when present.
        const root = this.closest(THEME_ADAPTER.productMainSelector);
        if (root && event.target !== root && !root.contains(event.target)) {
          return;
        }

        const next = this._productFromThemeDetail(detail);
        this.setProduct(next, {
          preservePersonalization: detail.preservePersonalization === true,
        });
      };

      this._bound.onVariantChange = () => {
        this._applyVariantImageFromForm();
      };

      document.addEventListener(
        THEME_ADAPTER.productChangeEvent,
        this._bound.onProductChange
      );

      const form = this._getProductForm();
      const variantSelect =
        form?.querySelector(THEME_ADAPTER.variantSelectSelector) ||
        document.querySelector(THEME_ADAPTER.variantSelectSelector);
      if (variantSelect) {
        variantSelect.addEventListener("change", this._bound.onVariantChange);
        this._variantSelect = variantSelect;
      }
    }

    _unbindThemeAdapter() {
      document.removeEventListener(
        THEME_ADAPTER.productChangeEvent,
        this._bound.onProductChange
      );
      if (this._variantSelect && this._bound.onVariantChange) {
        this._variantSelect.removeEventListener(
          "change",
          this._bound.onVariantChange
        );
      }
      this._variantSelect = null;
    }

    _productFromThemeDetail(detail) {
      const incoming = detail.product || {};
      const current = this.state.product;
      const productChanged =
        incoming.id != null && String(incoming.id) !== String(current.id);

      let images = current.images;
      let variants = current.variants;

      const hasPersonalizationImages =
        Array.isArray(incoming.images) &&
        incoming.images.some((img) => img && (img.focalX != null || img.srcPath));

      if (hasPersonalizationImages) {
        images = incoming.images.map(normalizeImage);
      } else if (productChanged && Array.isArray(incoming.images)) {
        // Alternate-color switch: theme JSON has no focal points — default to center.
        images = incoming.images.map((img) =>
          normalizeImage({
            id: img.id,
            url: img.src || img.url || "",
            width: img.width,
            height: img.height,
            alt: img.alt,
            focalX: 50,
            focalY: 50,
            srcPath: this._cdnPath(img.src || img.url || ""),
          })
        );
      }

      const hasPersonalizationVariants =
        Array.isArray(incoming.variants) &&
        incoming.variants.some((v) => v && (v.imageId != null || v.imageSrc != null));

      if (hasPersonalizationVariants) {
        variants = incoming.variants;
      } else if (productChanged && Array.isArray(incoming.variants)) {
        variants = incoming.variants.map((v) => ({
          id: v.id,
          imageId: v.image_id || v.imageId || null,
          imageSrc: this._cdnPath(v.featured_image?.src || v.image_src || ""),
          price: Number(v.price) || 0,
        }));
      }

      let image = current.image;

      if (detail.image) {
        image = normalizeImage(detail.image);
      } else if (detail.variant) {
        const matched = this._findImageForVariant(detail.variant, {
          images,
          variants,
        });
        if (matched) image = matched;
      } else if (incoming.image && (incoming.image.url || incoming.image.focalX != null)) {
        image = normalizeImage(incoming.image);
      } else if (productChanged) {
        const featuredUrl = incoming.featured_image_url || incoming.featured_image?.src;
        const featured =
          images.find((img) => featuredUrl && (img.url === featuredUrl || featuredUrl.includes(String(img.id)))) ||
          images[0];
        if (featured) {
          image = featured;
        } else if (featuredUrl) {
          image = normalizeImage({
            url: featuredUrl,
            alt: incoming.title || "",
            focalX: 50,
            focalY: 50,
          });
        }
      }

      return {
        id: incoming.id != null ? incoming.id : current.id,
        handle: incoming.handle || current.handle,
        title: incoming.title || current.title,
        vendor: incoming.vendor || current.vendor,
        imageSource: current.imageSource,
        image,
        images,
        variants,
      };
    }

    _cdnPath(url) {
      if (!url || typeof url !== "string") return "";
      const marker = "/cdn/";
      const idx = url.indexOf(marker);
      if (idx === -1) return url.startsWith("/cdn/") ? url : "";
      return url.slice(idx);
    }

    _findImageForVariant(variant, productData) {
      const images = Array.isArray(productData?.images)
        ? productData.images.map(normalizeImage)
        : this.state.product.images;
      const variants = Array.isArray(productData?.variants)
        ? productData.variants
        : this.state.product.variants;

      const variantId = variant?.id != null ? String(variant.id) : "";
      const meta = variants.find((v) => String(v.id) === variantId);

      if (meta?.imageId) {
        const byId = images.find((img) => String(img.id) === String(meta.imageId));
        if (byId) return byId;
      }

      if (meta?.imageSrc) {
        const bySrc = images.find(
          (img) => img.srcPath && meta.imageSrc.includes(img.srcPath.replace(/^\/cdn\//, ""))
            || img.srcPath === meta.imageSrc
        );
        if (bySrc) return bySrc;
      }

      if (variant?.image_src) {
        const byVariantSrc = images.find(
          (img) => img.srcPath === variant.image_src || (img.url && img.url.includes(String(variant.image_src).replace(/^\/cdn\//, "")))
        );
        if (byVariantSrc) return byVariantSrc;
      }

      return images[0] || this.state.product.image;
    }

    _applyVariantImageFromForm() {
      const form = this._getProductForm();
      const select =
        form?.querySelector(THEME_ADAPTER.variantSelectSelector) ||
        this._variantSelect;

      if (this.config.imageSource === "variant" && select?.value) {
        const variantMeta = this.state.product.variants.find(
          (v) => String(v.id) === String(select.value)
        );
        if (variantMeta) {
          const matched = this._findImageForVariant(
            { id: variantMeta.id, image_src: variantMeta.imageSrc },
            this.state.product
          );
          if (matched) {
            this.state.product.image = matched;
            this.updateState();
          }
        }
      }

      this._syncConfirmPrice();

      // Keep nested fee parent_id aligned with the selected variant.
      if (this.state.personalization.confirmed) {
        this._syncProperties();
      }
    }

    /**
     * Confirm CTA shows selected variant price + embroidery fee.
     * Reads the active variant from #product-select (updated on option change).
     */
    _syncConfirmPrice() {
      const el = this.els.confirmPrice;
      if (!el) return;

      const fee = Number(this.config.feePrice) || 0;
      const variantId = this._getParentVariantId();
      const variant = this.state.product.variants.find(
        (v) => String(v.id) === String(variantId)
      );
      let variantPrice = Number(variant?.price);
      if (!Number.isFinite(variantPrice) || variantPrice < 0) {
        variantPrice = this._lookupThemeVariantPrice(variantId);
      }
      if (!Number.isFinite(variantPrice) || variantPrice < 0) variantPrice = 0;

      const formatted = formatMoney(variantPrice + fee);
      if (formatted) el.textContent = formatted;
    }

    _lookupThemeVariantPrice(variantId) {
      if (!variantId) return NaN;
      const products = [window.Shopify?.product];
      const jsonEl = document.getElementById("product-json");
      if (jsonEl) {
        const parsed = parseJSON(jsonEl, null);
        if (parsed) products.push(parsed);
      }
      for (const product of products) {
        const match = product?.variants?.find(
          (v) => String(v.id) === String(variantId)
        );
        const price = Number(match?.price);
        if (Number.isFinite(price) && price >= 0) return price;
      }
      return NaN;
    }

    _observeResize() {
      if (typeof ResizeObserver !== "function" || !this.els.preview) return;
      this._resizeObserver = new ResizeObserver(() => this._fitText());
      this._resizeObserver.observe(this.els.preview);
      if (this.els.zone) this._resizeObserver.observe(this.els.zone);
      if (this.els.summaryPreviewZone) {
        this._resizeObserver.observe(this.els.summaryPreviewZone);
      }
    }

    /**
     * Render preview copy into an element. With monogram_font_style + exactly 3
     * characters, use small / large / small letter sizing.
     */
    _renderPreviewText(displayText, font, target) {
      const el = target || this.els.previewText;
      if (!el) return;

      if (!shouldRenderMonogram(font, displayText)) {
        el.textContent = displayText;
        return;
      }

      el.textContent = "";
      const wrap = document.createElement("span");
      wrap.className = "product-personalization__monogram";
      const roles = ["side", "center", "side"];
      [...displayText].forEach((ch, index) => {
        const span = document.createElement("span");
        span.className = `product-personalization__monogram-${roles[index]}`;
        span.textContent = ch;
        wrap.appendChild(span);
      });
      el.appendChild(wrap);
    }

    /**
     * Keep each style card sample in sync with typed text.
     * Until the shopper edits, each card shows that style's metaobject initial text.
     * Each option is clipped to that style's max character count.
     */
    _updateFontOptionSamples(text, colorValue) {
      const rawText = (text || "").trim();
      const currentFont = this._getFont(this.state.personalization.font);
      const isSample = isUneditedInitialText(rawText, currentFont);

      this.querySelectorAll("[data-personalization-font]").forEach((input) => {
        const sample = input
          .closest("label")
          ?.querySelector("[data-personalization-font-sample]");
        if (!sample) return;

        const maxChars = Math.max(
          1,
          Number(input.getAttribute("data-personalization-font-max-chars")) || 20
        );
        const initialText = (
          input.getAttribute("data-personalization-font-initial-text") || ""
        ).trim();
        const font = {
          capitalization: input.getAttribute("data-personalization-font-capitalization"),
          monogramFontStyle:
            input.getAttribute("data-personalization-font-monogram") === "true",
          initialText,
        };
        const source = (isSample ? initialText : rawText).slice(0, maxChars);
        const displayText = applyCapitalization(source, font.capitalization);
        this._renderPreviewText(displayText, font, sample);
        sample.classList.toggle(
          "product-personalization__font-sample--placeholder",
          isSample
        );
        if (colorValue) sample.style.color = colorValue;
      });
      this._fitFontOptionSamples();
    }

    _fitFontOptionSamples() {
      this.querySelectorAll("[data-personalization-font-sample]").forEach((sample) => {
        sample.style.fontSize = "";
        const maxSize = Math.max(
          8,
          Math.round(parseFloat(getComputedStyle(sample).fontSize) || 28)
        );
        const inset = 0;
        const safety = 2;
        const availableWidth = Math.max(1, sample.clientWidth - (inset + safety) * 2);
        const availableHeight = Math.max(1, sample.clientHeight - 8);
        if (availableWidth <= 0 || availableHeight <= 0) return;

        let size = maxSize;
        sample.style.whiteSpace = "nowrap";
        sample.style.fontSize = `${size}px`;
        while (size > 8) {
          const ink = measureTextInk(sample);
          const half = Math.max(0, (sample.clientWidth - (ink.advance || ink.width)) / 2);
          const leftClear = half - (ink.overflowLeft || 0);
          const rightClear = half - (ink.overflowRight || 0);
          const tooWide =
            ink.width > availableWidth ||
            leftClear < inset ||
            rightClear < inset;
          const tooTall = ink.height > availableHeight;
          if (!tooWide && !tooTall) break;
          size -= 1;
          sample.style.fontSize = `${size}px`;
        }
      });
    }

    /**
     * Apply the active style font to an element. Prefer the computed family from
     * the selected radio sample (already rendering correctly) so preview matches.
     */
    _applyFontToElement(el, font) {
      if (!el) return;

      const checked = this.querySelector("[data-personalization-font]:checked");
      const sample = checked
        ?.closest("label")
        ?.querySelector("[data-personalization-font-sample]");

      if (sample) {
        const computed = getComputedStyle(sample);
        el.style.fontFamily = computed.fontFamily;
        el.style.fontWeight = computed.fontWeight;
        return;
      }

      el.style.fontFamily = quoteFontFamily(font.family);
      el.style.fontWeight = font.weight || "400";
    }

    /**
     * Apply the active style font to the live product preview.
     */
    _applyPreviewFont(font) {
      this._applyFontToElement(this.els.previewText, font);
    }

    async _loadActiveFont() {
      if (!document.fonts?.load) return;
      const font = this._getFont(this.state.personalization.font);
      const family = quoteFontFamily(font.family);
      const weight = font.weight || "400";
      try {
        await document.fonts.load(`${weight} 64px ${family}`);
      } catch (_) {
        /* ignore load failures; preview keeps fallback stack */
      }
    }

    _getPreviewTextScale() {
      const raw = getComputedStyle(this)
        .getPropertyValue("--personalization-text-scale")
        .trim();
      let n = parseFloat(raw);
      if (Number.isFinite(n) && n > 0 && raw !== "" && !/[^0-9.+-eE]/.test(raw)) {
        return Math.min(1, n);
      }

      const probe = document.createElement("span");
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText =
        "position:absolute;width:0;height:0;overflow:hidden;opacity:var(--personalization-text-scale);pointer-events:none;";
      this.appendChild(probe);
      n = parseFloat(getComputedStyle(probe).opacity);
      probe.remove();
      if (Number.isFinite(n) && n > 0) return Math.min(1, n);

      const vw = window.innerWidth;
      if (vw > 767) return 1;
      const t = 0.5 + ((vw - 540) * 0.5) / (768 - 479);
      return Math.min(0.75, Math.max(0.475, t));
    }

    _getPreviewWidthScale() {
      const previewW =
        this.els.image?.clientWidth ||
        this.els.preview?.clientWidth ||
        0;
      const raw = getComputedStyle(this)
        .getPropertyValue("--personalization-preview-reference-width")
        .trim();
      const referenceW = parseFloat(raw) || 720;
      if (previewW <= 0 || referenceW <= 0) return 1;
      return previewW / referenceW;
    }

    _fitText() {
      const limits = this._getStyleLimits();
      const previewW =
        this.els.image?.clientWidth ||
        this.els.preview?.clientWidth ||
        0;
      const maxSize = Math.max(
        1,
        Math.round(limits.maxFontSize * this._getPreviewWidthScale())
      );
      const result = fitSingleLineText(
        this.els.previewText,
        this.els.zone,
        1,
        maxSize
      );
      if (result?.size) {
        this.style.setProperty(
          "--personalization-preview-font-size",
          `${result.size}px`
        );
        if (this.els.previewText && previewW > 0) {
          this.els.previewText.style.fontSize = `${(result.size / previewW) * 100}cqi`;
        }
      }
      this._fitSummaryPreview();
      this._fitFontOptionSamples();
      return result || { fits: true, size: limits.maxFontSize };
    }

    _fitSummaryPreview() {
      const el = this.els.summaryPreview;
      const zone = this.els.summaryPreviewZone;
      if (!el || !zone || this.els.summary?.hidden) return;

      const styles = getComputedStyle(zone);
      const inset = 6;
      const width = Math.max(
        1,
        zone.clientWidth -
          parseFloat(styles.paddingLeft) -
          parseFloat(styles.paddingRight) -
          inset * 2
      );
      const height = Math.max(
        1,
        zone.clientHeight -
          parseFloat(styles.paddingTop) -
          parseFloat(styles.paddingBottom) -
          inset * 2
      );
      const maxSize = Math.max(10, Math.floor(height * 0.42));
      fitSingleLineText(el, { clientWidth: width, clientHeight: height }, 6, maxSize);
    }

    _normalizeFonts(fonts) {
      if (!Array.isArray(fonts) || !fonts.length) return DEFAULT_FONTS.slice();
      return fonts
        .map((font) => ({
          id: font?.id != null ? String(font.id) : "",
          label: font?.label || (font?.id != null ? String(font.id) : ""),
          family: font?.family || "Arial, Helvetica, sans-serif",
          weight: font?.weight != null ? String(font.weight) : "400",
          style: font?.style || "normal",
          maxCharacters: Math.max(1, Number(font?.maxCharacters) || 20),
          maxFontSize: Math.max(1, Number(font?.maxFontSize) || 48),
          capitalization: normalizeCapitalization(font?.capitalization),
          monogramFontStyle: Boolean(font?.monogramFontStyle),
          initialText: String(font?.initialText || "").trim(),
        }))
        .filter((font) => font.id);
    }

    _normalizeColors(colors) {
      if (!Array.isArray(colors) || !colors.length) return [];
      return colors
        .map((color) => ({
          id: color?.id != null ? String(color.id) : "",
          label: color?.label || (color?.id != null ? String(color.id) : ""),
          value: normalizeCssColor(color?.value),
        }))
        .filter((color) => color.id);
    }

    _getFont(fontId) {
      const id = fontId != null ? String(fontId) : "";
      const fromConfig = this.config.fonts.find((f) => f.id === id);
      if (fromConfig) return fromConfig;

      const checked = this.querySelector(
        `[data-personalization-font][value="${CSS.escape(id)}"]`
      );
      if (checked) {
        return {
          id,
          label: checked.getAttribute("data-personalization-font-label") || id,
          family:
            checked.getAttribute("data-personalization-font-family") ||
            "Arial, Helvetica, sans-serif",
          weight: checked.getAttribute("data-personalization-font-weight") || "400",
          style: checked.getAttribute("data-personalization-font-style") || "normal",
          maxCharacters: Math.max(
            1,
            Number(checked.getAttribute("data-personalization-font-max-chars")) || 20
          ),
          maxFontSize: Math.max(
            1,
            Number(checked.getAttribute("data-personalization-font-max-font")) || 48
          ),
          capitalization: normalizeCapitalization(
            checked.getAttribute("data-personalization-font-capitalization")
          ),
          monogramFontStyle:
            checked.getAttribute("data-personalization-font-monogram") === "true",
          initialText: (
            checked.getAttribute("data-personalization-font-initial-text") || ""
          ).trim(),
        };
      }

      return this.config.fonts[0] || DEFAULT_FONTS[0];
    }

    _getStyleLimits() {
      const font = this._getFont(this.state.personalization.font);
      return {
        maxCharacters: Math.max(
          1,
          Number(font.maxCharacters) || this.config.maxCharacters || 20
        ),
        maxFontSize: Math.max(1, Number(font.maxFontSize) || this.config.maxFontSize || 48),
      };
    }

    /** True when the input still holds sample/default copy the shopper has not replaced. */
    _isUneditedDefaultText() {
      if (this.state.personalization.confirmed) return false;
      const font = this._getFont(this.state.personalization.font);
      const text = this.state.personalization.text;
      if (isUneditedInitialText(text, font)) return Boolean(String(text || "").trim());
      const fallback = String(this.config.defaultText || "").trim();
      return Boolean(fallback) && String(text || "").trim() === fallback;
    }

    /** Clear sample text when the shopper clicks into the modal input. */
    _clearUneditedDefaultText() {
      if (!this._isUneditedDefaultText()) return false;
      this.state.personalization.text = "";
      if (this.els.textInput) this.els.textInput.value = "";
      this.state.ui.error = "";
      this.updateState();
      return true;
    }

    /**
     * Modal open auto-focuses the input, so typing would append to the sample.
     * Drop the sample first when the shopper starts entering their own text.
     */
    _replaceUneditedDefaultTextOnKey(event) {
      if (!this._isUneditedDefaultText()) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const inserting = event.key.length === 1;
      const deleting = event.key === "Backspace" || event.key === "Delete";
      if (!inserting && !deleting) return;
      this.state.personalization.text = "";
      event.target.value = "";
      if (deleting) {
        event.preventDefault();
        this.updateState();
      }
    }

    /**
     * Sync input maxlength + char-max label to the active style.
     * Optionally truncate text when switching to a shorter max.
     */
    _applyStyleLimits(options = {}) {
      const limits = this._getStyleLimits();

      if (this.els.textInput) {
        this.els.textInput.maxLength = limits.maxCharacters;
      }
      if (this.els.charMax) {
        this.els.charMax.textContent = String(limits.maxCharacters);
      }

      if (options.truncateText) {
        const text = this.state.personalization.text || "";
        if (text.length > limits.maxCharacters) {
          const next = text.slice(0, limits.maxCharacters);
          this.state.personalization.text = next;
          if (this.els.textInput) this.els.textInput.value = next;
        }
      }
    }

    _getColor(colorId) {
      const id = colorId != null ? String(colorId) : "";

      // Prefer the radio’s data attribute — Liquid prints color fields as CSS strings there.
      const radio =
        (id &&
          this.querySelector(
            `[data-personalization-color][value="${CSS.escape(id)}"]`
          )) ||
        this.querySelector("[data-personalization-color]:checked") ||
        this.querySelector("[data-personalization-color]");

      if (radio) {
        const fromDom = radio.getAttribute("data-personalization-color-value");
        if (fromDom) {
          return {
            id: String(radio.value || id),
            label:
              radio.getAttribute("data-personalization-color-label") ||
              String(radio.value || id),
            value: fromDom.trim(),
          };
        }
      }

      const fromConfig = this.config.colors.find((c) => c.id === id);
      if (fromConfig) {
        return {
          id: fromConfig.id,
          label: fromConfig.label || fromConfig.id,
          value: normalizeCssColor(fromConfig.value),
        };
      }

      return this.config.colors[0]
        ? {
            id: this.config.colors[0].id,
            label: this.config.colors[0].label,
            value: normalizeCssColor(this.config.colors[0].value),
          }
        : { id: "", label: "", value: "#1a1a1a" };
    }
    _getProductForm() {
      const formId = this.getAttribute("data-personalization-form-id") || "AddToCartForm";
      return THEME_ADAPTER.productFormSelector(formId);
    }

    _getAddToCartButton() {
      const form = this._getProductForm();
      return (
        form?.querySelector("#AddToCart") ||
        document.getElementById("AddToCart") ||
        form?.querySelector('[name="add"]') ||
        form?.querySelector('[type="submit"]')
      );
    }

    _getCartPageUrl() {
      return (
        this.getAttribute("data-personalization-cart-url") ||
        THEME_ADAPTER.cartPageUrl ||
        "/cart"
      );
    }

    _setConfirmBusy(busy, label) {
      const btn = this.els.confirmBtn;
      if (!btn) return;
      if (busy) {
        if (this._confirmBtnHtml == null) this._confirmBtnHtml = btn.innerHTML;
        btn.disabled = true;
        if (label) btn.textContent = label;
        return;
      }
      btn.disabled = false;
      if (this._confirmBtnHtml != null) {
        btn.innerHTML = this._confirmBtnHtml;
        this._confirmBtnHtml = null;
        this.els.confirmPrice = btn.querySelector(
          "[data-personalization-confirm-price]"
        );
        this._syncConfirmPrice();
      }
    }

    /**
     * Same path as clicking #AddToCart: nested fee uses Cart AJAX JSON,
     * otherwise the theme AjaxifyCart submit handler runs.
     */
    async _addToCartFromConfirm() {
      const form = this._getProductForm();
      const addBtn = this._getAddToCartButton();

      if (!form || !addBtn) {
        this.state.ui.error = "Could not add this item to your bag.";
        this.updateState();
        return false;
      }

      if (addBtn.disabled) {
        this.state.ui.error = /sold out/i.test(addBtn.textContent || "")
          ? "This item is sold out."
          : "This item cannot be added to your bag.";
        this.updateState();
        return false;
      }

      this._setConfirmBusy(true, "Adding...");
      this._lastAddPromise = null;

      let added = false;
      try {
        const ajaxWait = this._shouldNestFee() ? null : this._listenForAjaxifyCartAdd();
        addBtn.click();

        if (this._shouldNestFee()) {
          added = await (this._lastAddPromise || this._addNestedCartLines(form));
        } else {
          added = await ajaxWait;
        }

        if (!added) {
          this.state.ui.error =
            this.state.ui.formError ||
            "Could not add personalized item to your bag.";
          this.updateState();
        }
        return Boolean(added);
      } finally {
        if (!added) this._setConfirmBusy(false);
      }
    }

    _listenForAjaxifyCartAdd() {
      return new Promise((resolve) => {
        const $ = window.jQuery;
        let settled = false;
        const isCartAdd = (settings) =>
          String(settings?.url || "").includes("/cart/add");

        const done = (ok) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          if ($) {
            $(document).off("ajaxSuccess", onSuccess);
            $(document).off("ajaxError", onError);
          }
          resolve(Boolean(ok));
        };

        const onSuccess = (_event, _xhr, settings) => {
          if (isCartAdd(settings)) done(true);
        };
        const onError = (_event, _xhr, settings) => {
          if (isCartAdd(settings)) done(false);
        };

        const timer = window.setTimeout(() => done(false), 20000);

        if ($) {
          $(document).on("ajaxSuccess", onSuccess);
          $(document).on("ajaxError", onError);
        }
      });
    }

    _attachFormSubmitListener() {
      const form = this._getProductForm();
      if (!form || this._formSubmitBound) return;
      form.addEventListener("submit", this._bound.onFormSubmit, true);
      this._formSubmitBound = true;
      this._boundForm = form;
    }

    _removeFormSubmitListener() {
      if (this._boundForm && this._bound.onFormSubmit) {
        this._boundForm.removeEventListener(
          "submit",
          this._bound.onFormSubmit,
          true
        );
      }
      this._formSubmitBound = false;
      this._boundForm = null;
    }

    _handleFormSubmit(event) {
      if (this.config.required) {
        const ok =
          this.state.personalization.enabled &&
          this.state.personalization.confirmed &&
          this.state.personalization.text.trim();
        if (!ok) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.state.ui.formError =
            "Please personalize this item before adding it to your bag.";
          this.updateState();
          this.open(this.els.openBtn);
          this.els.textInput?.focus();
          return;
        }
        this.state.ui.formError = "";
        this.updateState();
      }

      // AjaxifyCart serializes the form; nest fee via Cart AJAX JSON when configured.
      // shopify-additional-items inputs remain for accelerated checkout wallets.
      if (!this._shouldNestFee()) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this._lastAddPromise = this._addNestedCartLines(this._getProductForm());
    }

    _isPersonalizationActive() {
      return (
        this.state.personalization.enabled &&
        this.state.personalization.confirmed &&
        Boolean(this.state.personalization.text.trim())
      );
    }

    _shouldNestFee() {
      if (!this._isPersonalizationActive()) return false;
      const feeId = this.config.feeVariantId;
      if (!feeId) return false;
      const parentId = this._getParentVariantId();
      if (!parentId) return false;
      return String(feeId) !== String(parentId);
    }

    _getParentVariantId() {
      const form = this._getProductForm();
      if (!form) return "";
      const field =
        form.querySelector(THEME_ADAPTER.variantSelectSelector) ||
        form.querySelector('[name="id"]');
      return field?.value ? String(field.value) : "";
    }

    _buildPropertyMap() {
      const font = this._getFont(this.state.personalization.font);
      const color = this._getColor(this.state.personalization.color);
      return {
        [PROPERTY_KEYS.timeStamp]: buildTimeStamp(),
        [PROPERTY_KEYS.placement]: this.config.embroideryPlacement || "",
        [PROPERTY_KEYS.text]: this.state.personalization.text.trim(),
        [PROPERTY_KEYS.style]: font.label,
        [PROPERTY_KEYS.color]: color.label,
        ...(this.state.extraProperties || {}),
      };
    }

    async _addNestedCartLines(form) {
      if (!form || this._nestedAddInFlight) return false;

      const parentId = this._getParentVariantId();
      const feeId = this.config.feeVariantId;
      if (!parentId || !feeId) return false;

      const parentVariantId = Number(parentId);
      const feeVariantId = Number(feeId);
      if (!Number.isFinite(parentVariantId) || parentVariantId <= 0) {
        this.state.ui.formError =
          `Cannot add to cart: product variant id is invalid (${parentId || "empty"}).`;
        this.updateState();
        console.error("product-personalization: invalid parent variant id", {
          parentId,
          feeId,
        });
        return false;
      }
      if (!Number.isFinite(feeVariantId) || feeVariantId <= 0) {
        this.state.ui.formError =
          `Cannot add to cart: personalization fee variant id is invalid (${feeId || "empty"}). Check fee_product_variant on the personalization metaobject.`;
        this.updateState();
        console.error("product-personalization: invalid fee variant id", {
          parentId,
          feeId,
        });
        return false;
      }

      const qtyInput = form.querySelector('[name="quantity"]');
      const quantity = Math.max(1, parseInt(qtyInput?.value, 10) || 1);
      const properties = this._buildPropertyMap();
      Object.keys(properties).forEach((key) => {
        if (properties[key] === "" || properties[key] == null) delete properties[key];
      });

      const payload = {
        items: [
          {
            id: parentVariantId,
            quantity,
            properties,
          },
          {
            id: feeVariantId,
            quantity,
            parent_id: parentVariantId,
            properties: {
              _pp_nest_uid: `${parentVariantId}-${Date.now()}`,
            },
          },
        ],
      };

      const addBtn =
        form.querySelector("#AddToCart") ||
        form.querySelector('[name="add"]') ||
        form.querySelector('[type="submit"]');
      const originalLabel = addBtn?.textContent;

      this._nestedAddInFlight = true;
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.textContent = "Adding...";
      }

      try {
        const response = await fetch(THEME_ADAPTER.cartAddUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          const description =
            errBody.description || errBody.message || "Could not add to cart";
          console.error("product-personalization: nested add failed", {
            status: response.status,
            description,
            errBody,
            payload,
            hint:
              description === "Cannot find variant"
                ? "Usually the fee_product_variant is unpublished on Online Store, deleted, or not a variant id. Parent=" +
                  parentVariantId +
                  " Fee=" +
                  feeVariantId
                : undefined,
          });
          throw new Error(
            this._formatCartAddError(description, parentVariantId, feeVariantId)
          );
        }

        if (addBtn) addBtn.textContent = "Added!";
        await this._refreshCartCount(quantity);

        window.setTimeout(() => {
          if (addBtn) {
            addBtn.disabled = false;
            addBtn.textContent = originalLabel || "Add to Shopping Bag";
          }
        }, 1000);
        return true;
      } catch (err) {
        console.warn("product-personalization: nested add failed", err);
        this.state.ui.formError =
          err?.message || "Could not add personalized item to your bag.";
        this.updateState();
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.textContent = originalLabel || "Add to Shopping Bag";
        }
        return false;
      } finally {
        this._nestedAddInFlight = false;
      }
    }

    _formatCartAddError(description, parentVariantId, feeVariantId) {
      if (description === "Cannot find variant") {
        return [
          "Cannot find variant.",
          `Product variant: ${parentVariantId}.`,
          `Fee variant: ${feeVariantId}.`,
          "Check that fee_product_variant on the personalization metaobject is a real variant published to the Online Store channel.",
        ].join(" ");
      }
      return description;
    }

    async _refreshCartCount(addedQuantity) {
      try {
        const cart = await fetch(THEME_ADAPTER.cartUrl, {
          headers: { Accept: "application/json" },
        }).then((r) => r.json());
        THEME_ADAPTER.onCartUpdated(cart, { addedQuantity });
      } catch (_) {
        /* ignore */
      }
    }

    _syncProperties() {
      const form = this._getProductForm();
      if (!form) return;

      form
        .querySelectorAll(
          "[data-personalization-property], [data-personalization-additional-item]"
        )
        .forEach((el) => el.remove());

      if (!this._isPersonalizationActive()) return;

      const values = this._buildPropertyMap();

      // Insert into the form itself — Shopify.AjaxifyCart uses jQuery serialize(),
      // which does not include external inputs that only use the HTML form attribute.
      Object.entries(values).forEach(([key, value]) => {
        if (value === "" || value == null) return;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[${key}]`;
        input.value = String(value);
        input.setAttribute("data-personalization-property", key);
        form.appendChild(input);
      });

      this._syncAdditionalItems(form);
    }

    /**
     * Nested fee for accelerated checkout (shopify-additional-items).
     * @see https://shopify.dev/docs/storefronts/themes/pricing-payments/accelerated-checkout/multiple-product-checkout
     */
    _syncAdditionalItems(form) {
      const target = form || this._getProductForm();
      if (!target) return;

      target
        .querySelectorAll("[data-personalization-additional-item]")
        .forEach((el) => el.remove());

      if (!this._shouldNestFee()) return;

      const feeId = this.config.feeVariantId;
      const parentId = this._getParentVariantId();

      const fields = [
        { name: "shopify-additional-items[1][id]", value: feeId },
        { name: "shopify-additional-items[1][parent_id]", value: parentId },
        { name: "shopify-additional-items[1][quantity]", value: "1" },
      ];

      fields.forEach(({ name, value }) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = String(value);
        input.setAttribute("data-personalization-additional-item", "1");
        target.appendChild(input);
      });
    }
  }

  if (!customElements.get("product-personalization")) {
    customElements.define("product-personalization", ProductPersonalization);
  }
})();
